import * as React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { OTPInputContext } from "input-otp";
import {
  InputOTP as BaseInputOTP,
  InputOTPGroup as BaseInputOTPGroup,
  InputOTPSeparator as BaseInputOTPSeparator,
} from "@/shared/ui/components/ui/input-otp";
import { cn } from "@/shared/lib/utils";

const OTPErrorContext = React.createContext<{
  error?: string | boolean;
  disabled?: boolean;
}>({});

export interface InputOTPBaseProps {
  maxLength: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  label?: string;
  error?: string | boolean;
  textAlign?: "center" | "left" | "right";
  pushPasswordManagerStrategy?: "none" | "increase-width";
  pasteTransformer?: (pasted: string) => string;
  children: React.ReactNode;
}

export interface InputOTPProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<
    InputOTPBaseProps,
    "name" | "value" | "onChange" | "error" | "children"
  > {
  name?: TName;
  control?: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  value?: string;
  onChange?: (value: string) => void;
  error?: string | boolean;
  children: React.ReactNode;
}

const InputOTPBase = React.forwardRef<
  React.ElementRef<typeof BaseInputOTP>,
  InputOTPBaseProps
>(
  (
    {
      className,
      containerClassName,
      label,
      error,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const hasError = Boolean(error);
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (
        !/[0-9]/.test(e.key) &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(
          e.key
        )
      ) {
        e.preventDefault();
      }
    };
    return (
      <OTPErrorContext.Provider value={{ error, disabled }}>
        <div className="space-y-2">
          {label && (
            <label className="text-sm font-medium text-grey-900">{label}</label>
          )}
          <BaseInputOTP
            onKeyDown={handleKeyDown}
            ref={ref}
            disabled={disabled}
            containerClassName={cn(
              "flex items-center gap-4",
              containerClassName
            )}
            className={cn("disabled:cursor-not-allowed", className)}
            {...props}
          >
            {children}
          </BaseInputOTP>
          {hasError && typeof error === "string" && (
            <p className="text-sm text-error-700">{error}</p>
          )}
        </div>
      </OTPErrorContext.Provider>
    );
  }
);
InputOTPBase.displayName = "InputOTPBase";

const InputOTP = React.forwardRef<
  React.ElementRef<typeof BaseInputOTP>,
  InputOTPProps<FieldValues, FieldPath<FieldValues>>
>((props, ref) => {
  const {
    name,
    control,
    rules,
    error: errorProp,
    children,
    ...restProps
  } = props;

  if (control && name) {
    return (
      <Controller
        name={name as any}
        control={control as any}
        rules={rules as any}
        render={({ field, fieldState }) => (
          <InputOTPBase
            {...restProps}
            value={field.value ?? ""}
            onChange={(value) => {
              field.onChange(value);
              restProps.onComplete?.(value);
            }}
            error={
              fieldState.error?.message ||
              (fieldState.error && true) ||
              errorProp
            }
            ref={ref}
          >
            {children}
          </InputOTPBase>
        )}
      />
    );
  }

  return (
    <InputOTPBase {...restProps} error={errorProp} ref={ref}>
      {children}
    </InputOTPBase>
  );
});

InputOTP.displayName = "InputOTP";

const InputOTPGroup = React.forwardRef<
  React.ElementRef<typeof BaseInputOTPGroup>,
  React.ComponentPropsWithoutRef<typeof BaseInputOTPGroup>
>(({ className, ...props }, ref) => (
  <BaseInputOTPGroup
    ref={ref}
    className={cn("flex items-center gap-2", className)}
    {...props}
  />
));
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div"> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { error, disabled } = React.useContext(OTPErrorContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];
  const hasError = Boolean(error);

  return (
    <div
      ref={ref}
      className={cn(
        "relative flex h-10 w-10 sm:h-14 sm:w-14 p-4 items-center justify-center border text-sm font-medium rounded-lg transition-colors",
        !disabled && "bg-white",
        disabled && "bg-grey-50",
        !hasError && !isActive && !disabled && "border-grey-100 text-grey-900",
        isActive &&
          !hasError &&
          !disabled &&
          "border-brand-green text-grey-900",
        hasError && !disabled && "border-error-700 bg-error-bg text-grey-900",
        hasError &&
          disabled &&
          "border-error-700 bg-error-bg text-grey-400 cursor-not-allowed",
        disabled &&
          !hasError &&
          "border-grey-100 text-grey-300 cursor-not-allowed",
        className
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && !disabled && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-caret-blink bg-grey-900 duration-1000" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = "InputOTPSlot";

const InputOTPSeparator = BaseInputOTPSeparator;
InputOTPSeparator.displayName = "InputOTPSeparator";

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
